import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Sparkles, Shirt, Plus, Trash2, Save, MapPin, Clock, Package, ChevronRight, MessageCircle } from 'lucide-react';
import { ChatDrawer } from '@/components/common/ChatDrawer';

const STATUS_LABELS = {
    requested: { label: 'Solicitado', tone: 'bg-blue-100 text-blue-800', next: 'picked_up', nextLabel: 'Marcar recolectado' },
    picked_up: { label: 'Recolectado', tone: 'bg-sky-100 text-sky-800', next: 'processing', nextLabel: 'En proceso' },
    processing: { label: 'En proceso', tone: 'bg-amber-100 text-amber-800', next: 'ready', nextLabel: 'Listo' },
    ready: { label: 'Listo', tone: 'bg-lime-100 text-lime-800', next: 'out_for_delivery', nextLabel: 'En entrega' },
    out_for_delivery: { label: 'En entrega', tone: 'bg-violet-100 text-violet-800', next: 'delivered', nextLabel: 'Marcar entregado' },
    delivered: { label: 'Entregado', tone: 'bg-emerald-100 text-emerald-800', next: null },
    cancelled: { label: 'Cancelado', tone: 'bg-rose-100 text-rose-800', next: null },
};

const DEFAULT_CONFIG = {
    kind: 'both', name: '', description: '', tariff_mode: 'mixed',
    pricelist: [], zones: [], slots: ['morning', 'afternoon'],
    pickup_fee: 0, delivery_fee: 0, min_order_amount: 0,
    phone: '', turnaround_hours: 48, active: true,
};

export default function DashboardServicesProvider() {
    const [tab, setTab] = useState('config');
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [hasProvider, setHasProvider] = useState(false);
    const [orders, setOrders] = useState([]);
    const [zonesText, setZonesText] = useState('');
    const [busy, setBusy] = useState(false);
    const [chatOrder, setChatOrder] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/me/services/provider');
            if (data.provider) {
                setConfig({ ...DEFAULT_CONFIG, ...data.provider });
                setZonesText((data.provider.zones || []).join(', '));
                setHasProvider(true);
            }
        } catch (e) { toast.error('Error al cargar'); }
    }, []);

    const loadOrders = useCallback(async () => {
        try {
            const { data } = await api.get('/me/services/orders');
            setOrders(data.items || []);
        } catch (e) {}
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (tab === 'orders') loadOrders(); }, [tab, loadOrders]);

    const save = async () => {
        setBusy(true);
        try {
            const zones = zonesText.split(',').map(z => z.trim()).filter(Boolean);
            const payload = { ...config, zones };
            payload.pickup_fee = parseFloat(payload.pickup_fee) || 0;
            payload.delivery_fee = parseFloat(payload.delivery_fee) || 0;
            payload.min_order_amount = parseFloat(payload.min_order_amount) || 0;
            payload.turnaround_hours = parseInt(payload.turnaround_hours) || 48;
            payload.pricelist = (payload.pricelist || []).map(it => ({ ...it, price: parseFloat(it.price) || 0 }));
            await api.put('/me/services/provider', payload);
            toast.success(hasProvider ? 'Cambios guardados' : '¡Tu servicio está activo!');
            setHasProvider(true);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); } finally { setBusy(false); }
    };

    const addPriceItem = () => setConfig(c => ({ ...c, pricelist: [...(c.pricelist || []), { key: `item_${Date.now()}`, label: '', unit: 'piece', price: 0 }] }));
    const removePriceItem = (i) => setConfig(c => ({ ...c, pricelist: c.pricelist.filter((_, idx) => idx !== i) }));
    const updatePriceItem = (i, patch) => setConfig(c => ({ ...c, pricelist: c.pricelist.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));

    const advanceStatus = async (o) => {
        const next = STATUS_LABELS[o.status]?.next;
        if (!next) return;
        try {
            await api.post(`/me/services/orders/${o.id}/status`, { status: next });
            toast.success(`Estado actualizado: ${STATUS_LABELS[next].label}`);
            loadOrders();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    return (
        <div className="space-y-6" data-testid="dashboard-services-provider">
            <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-[hsl(var(--primary))]" />
                <h1 className="text-2xl font-semibold">Mis servicios (Lavandería / Tintorería)</h1>
                {config.active ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="config" data-testid="svc-tab-config">Configuración</TabsTrigger>
                    <TabsTrigger value="orders" data-testid="svc-tab-orders">Pedidos recibidos ({orders.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="config" className="pt-4 space-y-4">
                    <Card>
                        <CardContent className="p-5 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <Label>Tipo de servicio</Label>
                                    <Select value={config.kind} onValueChange={(v) => setConfig({ ...config, kind: v })}>
                                        <SelectTrigger data-testid="svc-kind"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="laundry">Solo Lavandería</SelectItem>
                                            <SelectItem value="drycleaning">Solo Tintorería</SelectItem>
                                            <SelectItem value="both">Lavandería + Tintorería</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="md:col-span-2">
                                    <Label>Nombre público</Label>
                                    <Input value={config.name} onChange={(e) => setConfig({ ...config, name: e.target.value })} placeholder="Ej: Lavandería Express Roma" data-testid="svc-name" />
                                </div>
                            </div>
                            <div>
                                <Label>Descripción</Label>
                                <Textarea rows={2} value={config.description || ''} onChange={(e) => setConfig({ ...config, description: e.target.value })} placeholder="Describe tu servicio, especialidad, tiempos, garantías…" />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <Label>Tarifa recolección</Label>
                                    <Input type="number" value={config.pickup_fee} onChange={(e) => setConfig({ ...config, pickup_fee: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Tarifa entrega</Label>
                                    <Input type="number" value={config.delivery_fee} onChange={(e) => setConfig({ ...config, delivery_fee: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Mínimo de orden</Label>
                                    <Input type="number" value={config.min_order_amount} onChange={(e) => setConfig({ ...config, min_order_amount: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Tiempo entrega (h)</Label>
                                    <Input type="number" value={config.turnaround_hours} onChange={(e) => setConfig({ ...config, turnaround_hours: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Zonas cubiertas (CP o barrios, separados por coma)</Label>
                                    <Input value={zonesText} onChange={(e) => setZonesText(e.target.value)} placeholder="06100, 06600, Roma Norte, Condesa" data-testid="svc-zones" />
                                </div>
                                <div>
                                    <Label>Teléfono contacto</Label>
                                    <Input value={config.phone || ''} onChange={(e) => setConfig({ ...config, phone: e.target.value })} placeholder="5512345678" />
                                </div>
                            </div>
                            <div className="flex items-center gap-4 pt-1">
                                <div className="flex items-center gap-2">
                                    <Switch checked={config.slots.includes('morning')} onCheckedChange={(v) => setConfig({ ...config, slots: v ? [...new Set([...config.slots, 'morning'])] : config.slots.filter(s => s !== 'morning') })} />
                                    <Label>Mañana (9-13)</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch checked={config.slots.includes('afternoon')} onCheckedChange={(v) => setConfig({ ...config, slots: v ? [...new Set([...config.slots, 'afternoon'])] : config.slots.filter(s => s !== 'afternoon') })} />
                                    <Label>Tarde (14-18)</Label>
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                    <Switch checked={!!config.active} onCheckedChange={(v) => setConfig({ ...config, active: v })} data-testid="svc-active" />
                                    <Label>Publicar</Label>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold">Lista de precios</h3>
                                <Button size="sm" onClick={addPriceItem} data-testid="svc-add-price"><Plus className="h-3 w-3 mr-1" />Agregar</Button>
                            </div>
                            <div className="space-y-2">
                                {(config.pricelist || []).length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Agrega prendas o unidades de precio.</div>}
                                {(config.pricelist || []).map((it, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 rounded border">
                                        <div className="col-span-4">
                                            <Label className="text-xs">Nombre visible</Label>
                                            <Input value={it.label} onChange={(e) => updatePriceItem(i, { label: e.target.value })} placeholder="Camisa" />
                                        </div>
                                        <div className="col-span-3">
                                            <Label className="text-xs">Clave</Label>
                                            <Input value={it.key} onChange={(e) => updatePriceItem(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="shirt" />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="text-xs">Unidad</Label>
                                            <Select value={it.unit} onValueChange={(v) => updatePriceItem(i, { unit: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="piece">Pieza</SelectItem>
                                                    <SelectItem value="kg">Kilo</SelectItem>
                                                    <SelectItem value="bundle">Paquete</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="text-xs">Precio $</Label>
                                            <Input type="number" value={it.price} onChange={(e) => updatePriceItem(i, { price: e.target.value })} />
                                        </div>
                                        <div className="col-span-1">
                                            <Button size="icon" variant="ghost" onClick={() => removePriceItem(i)} data-testid={`svc-del-price-${i}`}>
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex justify-end">
                        <Button onClick={save} disabled={busy} data-testid="svc-save"><Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : hasProvider ? 'Guardar cambios' : 'Publicar mi servicio'}</Button>
                    </div>
                </TabsContent>

                <TabsContent value="orders" className="pt-4 space-y-2">
                    {orders.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">Sin pedidos aún.</div>}
                    {orders.map(o => {
                        const meta = STATUS_LABELS[o.status] || STATUS_LABELS.requested;
                        return (
                            <Card key={o.id} data-testid={`svc-order-${o.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="font-semibold">#{o.id.slice(0, 8)}</div>
                                                <Badge className={meta.tone}>{meta.label}</Badge>
                                                <Badge variant="outline">{o.kind === 'drycleaning' ? 'Tintorería' : 'Lavandería'}</Badge>
                                                <span className="text-xs text-muted-foreground">Cliente: @{o.buyer_nickname}</span>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 text-xs">
                                                <div className="flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 shrink-0" /><span className="truncate">{o.pickup_address}</span></div>
                                                <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{o.pickup_date} · {o.pickup_slot === 'morning' ? 'Mañana' : 'Tarde'}</div>
                                                <div className="flex items-center gap-1"><Package className="h-3 w-3" />{o.items.length} item(s) · <strong>${o.total}</strong></div>
                                            </div>
                                            {o.notes && <div className="text-xs text-muted-foreground mt-1 italic">“{o.notes}”</div>}
                                        </div>
                                    <div className="flex flex-wrap gap-2 items-center">
                                        {meta.next && (
                                            <Button size="sm" onClick={() => advanceStatus(o)} data-testid={`svc-advance-${o.id}`}>
                                                {meta.nextLabel}<ChevronRight className="h-3 w-3 ml-1" />
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => setChatOrder(o)} data-testid={`svc-prov-chat-${o.id}`}>
                                            <MessageCircle className="h-3 w-3 mr-1" />Chat
                                        </Button>
                                    </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </TabsContent>
            </Tabs>

            {chatOrder && (
                <ChatDrawer
                    open={!!chatOrder}
                    onOpenChange={(v) => !v && setChatOrder(null)}
                    mode="order"
                    orderId={chatOrder.id}
                    title={`Chat con @${chatOrder.buyer_nickname} · #${chatOrder.id.slice(0, 8)}`}
                />
            )}
        </div>
    );
}
